/**
 * Centrale nucléaire (author's map, not in the GDD): a plant of four levels on an island: ground floor, two floors and
 * the roof, tied by ramps, with a reactor hall (a core of lava in an obsidian vessel, its cooling pool under it) and a
 * tank of acid in the basement, both behind obsidian. On the top floor a conveyor brings an acid barrel every turn
 * (MapState.belts): it drops through a hole to the floor below and rolls on, leaving its acid. Two cooling towers on the
 * shore smoke (MapState.vents). Drawn: the seed changes nothing here.
 */
import { Physique, sinDeg, TRIG_ONE } from '@invaders/sim';
import { FP, plainMap, type Belt, type Box, type MapState } from '../state';
import { type Gen, ellipse, island, layer, rect, sea, settle } from './common';

const { EMPTY, ROCK, SAND, WATER, LAVA, OBSIDIAN, ACID } = Physique;

const LEFT = 190;
const RIGHT = 834;
const WALL = 12;
const SLAB = 8;
/** Surface rows of the levels: ground floor, first, second (the conveyor's), roof. */
export const REACTOR_LEVELS = [356, 296, 236, 176];
/** The reactor hall, open from the ground floor to the roof; a gangway crosses it on the second floor. */
const HALL_X0 = 600;
const HALL_X1 = 760;
export const REACTOR_BELT: Belt = { x0: 400, x1: 560, y: 236, dir: 1, speed: FP >> 1, barrels: true };
/** The hole at the end of the conveyor (a small jump), and the wedge under it that sends the barrels back west. */
const DROP_X1 = 576;
const WEDGE_X0 = 530;

/** A ramp of 2 rows per column whose top is at (x, top), rising in direction `dir`: the slab behind that point is opened. */
function ramp(g: Gen, x: number, top: number, dir: 1 | -1): void {
  const run = Math.trunc((REACTOR_LEVELS[0] - REACTOR_LEVELS[1]) / 2);
  rect(g, x, top, x - dir * (run + 8), top + SLAB - 1, EMPTY);
  for (let k = 0; k <= run; k++) rect(g, x - dir * k, top + k * 2, x - dir * k, top + k * 2 + 4, ROCK);
}

export function drawReactor(g: Gen): MapState {
  const { seaY } = g;
  const [ground, first, second, roof] = REACTOR_LEVELS;
  const surface = island(g, 60, 964, 40, () => seaY - ground);

  // The shell: outer walls, slabs, roof.
  rect(g, LEFT, roof, RIGHT, ground - 1, ROCK);
  rect(g, LEFT + WALL, roof + SLAB, RIGHT - WALL, ground - 1, EMPTY);
  rect(g, LEFT + WALL, first, RIGHT - WALL, first + SLAB - 1, ROCK);
  rect(g, LEFT + WALL, second, RIGHT - WALL, second + SLAB - 1, ROCK);

  // Doors at both ends of the ground floor, windows on the floors.
  for (const x of [LEFT, RIGHT - WALL]) {
    rect(g, x, ground - 30, x + WALL, ground - 1, EMPTY);
    for (const y of [first, second]) rect(g, x, y - 34, x + WALL, y - 14, EMPTY);
  }

  // The hall: the slabs stop at its walls, its gangway stays.
  rect(g, HALL_X0, roof + SLAB, HALL_X1, ground - 1, EMPTY);
  rect(g, HALL_X0, second, HALL_X1, second + 3, ROCK);
  const cx = (HALL_X0 + HALL_X1) >> 1;
  const cy = first + 8;
  ellipse(g, cx, cy, 52, 40, OBSIDIAN);
  ellipse(g, cx, cy, 46, 34, LAVA);
  rect(g, cx - 34, cy + 36, cx + 34, ground - 1, OBSIDIAN, EMPTY);
  rect(g, HALL_X0, ground, HALL_X1, ground + 11, WATER, ROCK); // the cooling pool, sunk in the floor
  rect(g, cx - 34, ground, cx + 34, ground + 11, OBSIDIAN);

  // The ramps, a staircase going east from the western door: ground to first, first to second, second to the roof
  // (one climbs them walking east). Two more from the ground floor to the first: in the middle, and east of the hall.
  ramp(g, 250, first, 1);
  ramp(g, 340, second, 1);
  ramp(g, 384, roof, 1);
  ramp(g, 510, first, 1);
  ramp(g, 800, first, 1);

  // The conveyor: the hopper over its head, the hole at its end.
  rect(g, REACTOR_BELT.x0 + 2, roof + SLAB, REACTOR_BELT.x0 + 5, second - 26, ROCK);
  rect(g, REACTOR_BELT.x0 + 22, roof + SLAB, REACTOR_BELT.x0 + 25, second - 26, ROCK);
  rect(g, REACTOR_BELT.x1 + 1, second, DROP_X1, second + SLAB - 1, EMPTY);
  for (let x = WEDGE_X0; x < HALL_X0; x++) rect(g, x, first - Math.trunc((x - WEDGE_X0) / 3), x, first - 1, ROCK);

  // The cooling towers on the shore: hollow, a pool in the bottom.
  const vents: Box[] = [];
  for (const c of [132, 892]) {
    const height = 150;
    for (let up = 0; up < height; up++) {
      const half = 22 + Math.trunc((12 * Math.abs(sinDeg(Math.trunc((up * 200) / height) + 60))) / TRIG_ONE);
      const y = ground - 1 - up;
      rect(g, c - half - 5, y, c + half + 5, y, ROCK);
      rect(g, c - half, y, c + half, y, EMPTY);
    }
    rect(g, c - 20, ground - 14, c + 20, ground - 1, WATER);
    vents.push({ x0: c - 20, y0: ground - 14, x1: c + 20, y1: ground - 1 });
  }

  layer(g, surface, 40, 96, (x) => Math.min(5, Math.trunc(Math.abs(x - 40) / 8)), SAND);
  layer(g, surface, 928, 984, (x) => Math.min(5, Math.trunc(Math.abs(984 - x) / 8)), SAND);
  sea(g);

  // The basement: a tank of acid behind obsidian (it resists acid), under the sea's level.
  rect(g, 300, ground + 12, 560, ground + 66, OBSIDIAN);
  rect(g, 304, ground + 16, 556, ground + 62, ACID);
  settle(g);
  return { ...plainMap('reactor'), vents, belts: [REACTOR_BELT] };
}
